// Partners (M3): the foundry relationship, EDA tier & in-house tool programs,
// and the IP catalog — design §6.3/§6.4/§11 rendered as one screen.

import type { EdaTier } from "../../engine/types.js";
import { DISCIPLINES } from "../../engine/types.js";
import { interpolate } from "../../engine/calendar.js";
import { toolQuality } from "../../engine/systems/tools.js";
import type { ScreenProps } from "./shared.js";
import { money, pct } from "../format.js";
import { useNarrow } from "../media.js";
import { Bar, Stepper } from "../components/widgets.js";

const TIERS: EdaTier[] = ["basic", "standard", "premium"];

export function Partners({ snap, content, session }: ScreenProps) {
  const { view, pending } = snap;
  const narrow = useNarrow();
  const m = view.monthIndex;
  const sd = content.economy.stakeholders;
  const tl = content.economy.tools;
  const st = view.stakeholders;

  const crunch = interpolate(sd.crunch, m) >= 0.5;
  const rel = st.foundryRel;
  const relMood =
    rel >= sd.hotLotRelFloor
      ? "priority account — hot lots run half a month faster, wafers 3% off"
      : rel < sd.penaltyRelCeil
        ? "problem account — fab cycles +1 month, wafers +5%"
        : "account in good standing";

  const tierQueued = pending.find((c) => c.type === "setEdaTier");
  const shownTier = tierQueued && tierQueued.type === "setEdaTier" ? tierQueued.tier : view.tools.edaTier;

  const toolStaffQueued = (d: (typeof DISCIPLINES)[number]) => {
    const c = pending.find((x) => x.type === "setToolStaff" && x.discipline === d);
    return c && c.type === "setToolStaff" ? c.engineers : undefined;
  };

  return (
    <div>
      <h2>PARTNERS</h2>

      <div className="panel">
        <div className="title">
          FOUNDRY RELATIONSHIP{" "}
          {crunch && <span className="badge amber">CAPACITY CRUNCH</span>}
        </div>
        <Bar frac={rel / 100} mark={sd.hotLotRelFloor / 100} amber={rel < sd.penaltyRelCeil} label={`relationship ${rel.toFixed(0)}/100`} right={relMood} />
        <span className="hint">
          Node qualifications and steady wafer pull build the account; forecast busts burn it. In a crunch (’95, ’00),
          accounts under {sd.crunchSqueezeRelFloor} get squeezed to {pct(sd.crunchSqueezeFactor, 0)} of demand — allocation is politics.
        </span>
        {st.waferDiscountMonthsLeft > 0 && (
          <div className="hint">
            ◆ negotiated wafer discount: {pct(st.waferDiscountPct, 0)} for {st.waferDiscountMonthsLeft} more months
          </div>
        )}
        {st.volumeCommit && (
          <div className="warn-text">
            ◆ volume commitment: {Math.round(st.volumeCommit.unitsPerMonth).toLocaleString()} units/mo for {st.volumeCommit.monthsLeft} more
            months — shortfalls cost ${st.volumeCommit.penaltyPerUnit}/unit
          </div>
        )}
      </div>

      <div className="panel">
        <div className="title">EDA TOOLS — buy the flow (design §6.3)</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {TIERS.map((t) => (
            <button
              key={t}
              className={`act${shownTier === t ? " warn" : ""}`}
              onClick={() => session.queue({ type: "setEdaTier", tier: t })}
            >
              {t.toUpperCase()}
            </button>
          ))}
          <span className="hint">
            {shownTier === "basic" && "trailing-edge flows: 45% cheaper seats, ~5% slower methodology on NEW projects"}
            {shownTier === "standard" && "the market flow: the baseline every number in this game was tuned on"}
            {shownTier === "premium" && "early access + vendor support: seats cost 70% more, new projects plan ~4.5% faster"}
          </span>
        </div>
        <span className="hint">The tier is snapshotted into a project when it starts — mid-project flow changes are a lie vendors tell.</span>
      </div>

      <div className="panel">
        <div className="title">IN-HOUSE TOOLS — build the flow (the expert line, or the trap)</div>
        {narrow ? (
          // phones: quality bar full-width, numbers + team stepper on the next line
          <div>
            {DISCIPLINES.map((d) => {
              const tool = view.tools.inhouse[d];
              const q = toolQuality(tool.em, tl.inhouseTauEm);
              return (
                <div key={d} style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                  <Bar
                    frac={q}
                    amber={tool.staff === 0 && tool.em > 1}
                    label={`${d} ${q > 0.01 ? `— ${(q * 100).toFixed(0)}%` : ""}`}
                    right={tool.staff === 0 && tool.em > 1 ? "unmaintained — rotting" : ""}
                  />
                  <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                    <span className="hint">
                      −{pct(tl.licenseSavingsMax * q, 0)} licenses · +{pct(tl.prodBonusMax * q, 1)} productivity
                    </span>
                    <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                      <span className="dim">TEAM</span>
                      <Stepper value={tool.staff} queued={toolStaffQueued(d)} max={20} bigStep={2} onChange={(v) => session.queue({ type: "setToolStaff", discipline: d, engineers: v })} />
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th>domain</th>
                  <th style={{ width: 260 }}>quality</th>
                  <th className="r">license savings</th>
                  <th className="r">productivity</th>
                  <th className="r">TEAM</th>
                </tr>
              </thead>
              <tbody>
                {DISCIPLINES.map((d) => {
                  const tool = view.tools.inhouse[d];
                  const q = toolQuality(tool.em, tl.inhouseTauEm);
                  return (
                    <tr key={d}>
                      <td>{d}</td>
                      <td>
                        <Bar frac={q} amber={tool.staff === 0 && tool.em > 1} label={q > 0.01 ? `${(q * 100).toFixed(0)}%` : "—"} right={tool.staff === 0 && tool.em > 1 ? "unmaintained — rotting" : ""} />
                      </td>
                      <td className="r">−{pct(tl.licenseSavingsMax * q, 0)}</td>
                      <td className="r">+{pct(tl.prodBonusMax * q, 1)}</td>
                      <td className="r">
                        <Stepper value={tool.staff} queued={toolStaffQueued(d)} max={20} bigStep={2} onChange={(v) => session.queue({ type: "setToolStaff", discipline: d, engineers: v })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <span className="hint">
          Tool engineers are drawn AHEAD of your chips, like research programs. The payoff compounds — until the industry
          methodology shifts under your niche tool and its quality craters overnight. It has happened before. It will happen again.
        </span>
      </div>

      <div className="panel">
        <div className="title">IP CATALOG — license blocks instead of designing them (design §6.4)</div>
        <div className="scroll-x">
          <table>
            <thead>
              <tr>
                <th>block</th>
                <th>kind</th>
                <th className="r">design saved</th>
                <th className="r">upfront now</th>
                <th className="r">royalty</th>
                <th className="r">bug risk</th>
                <th>fit</th>
              </tr>
            </thead>
            <tbody>
              {content.ip
                .filter((ip) => ip.availableFrom <= m + 24)
                .map((ip) => {
                  const avail = ip.availableFrom <= m;
                  return (
                    <tr key={ip.id} className={avail ? "" : "inactive"}>
                      <td>{ip.displayName}</td>
                      <td>{ip.kind}</td>
                      <td className="r">{pct(ip.kGatesFrac, 0)}</td>
                      <td className="r">{avail ? money(interpolate(ip.upfront, m)) : `from ${Math.floor(ip.availableFrom / 12) + 1988}`}</td>
                      <td className="r">{pct(ip.royaltyPct, 1)}</td>
                      <td className="r">{ip.lambdaMult > 1.05 ? "⚠ bargain" : ip.lambdaMult < 1 ? "hardened" : "standard"}</td>
                      <td>{ip.pathFit === "neutral" ? "any" : ip.pathFit}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <span className="hint">
          Attach IP when STARTING a project (pipeline screen): effort and bug exposure shrink by the licensed share; the market
          still credits full complexity. The royalty never goes away — that's the deal.
        </span>
      </div>
    </div>
  );
}
